/**
 * Approvals Inbox — a single triage view across:
 *   1. Pending student registrations       (/api/users/pending)
 *   2. Pending enrollment requests         (/api/admin/enrollment-requests?status=pending)
 *   3. Pending payment verifications       (/api/admin/payments?status=pending_verification)
 *
 * The intent is triage speed: admins glance here once a day, click Review
 * (deep-links to the specific source page) or take a 1-click action on
 * registrations directly. The full detail flows still live on the source
 * pages so no functionality was moved — this is a *unified overview*.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasPerm } from "@/lib/permissions";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Inbox, UserCheck, ClipboardList, CreditCard, ArrowRight, CheckCircle2, XCircle, RefreshCw, Loader2,
} from "lucide-react";
import { toast } from "sonner";

const TYPE_STYLES = {
  registration: {
    label: "Registration",
    tone: "bg-indigo-100 text-indigo-800 border-indigo-200",
    icon: UserCheck,
  },
  enrollment: {
    label: "Enrollment",
    tone: "bg-amber-100 text-amber-800 border-amber-200",
    icon: ClipboardList,
  },
  payment: {
    label: "Payment",
    tone: "bg-emerald-100 text-emerald-800 border-emerald-200",
    icon: CreditCard,
  },
};

function formatWhen(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const diffH = (Date.now() - d.getTime()) / 36e5;
    if (diffH < 24) return `${Math.max(1, Math.round(diffH))}h ago`;
    if (diffH < 24 * 7) return `${Math.round(diffH / 24)}d ago`;
    return d.toLocaleDateString([], { month: "short", day: "numeric" });
  } catch {
    return "";
  }
}

export default function ApprovalsInboxPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [registrations, setRegistrations] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [payments, setPayments] = useState([]);
  const [busyIds, setBusyIds] = useState({}); // {id: true} while an action is in flight

  const canApprovePending = hasPerm(user, "pending_student", "approve");
  const canRejectPending = hasPerm(user, "pending_student", "reject");

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [regRes, enrRes, payRes] = await Promise.all([
        hasPerm(user, "pending_student", "view")
          ? api.get("/users/pending").then((r) => r.data).catch(() => [])
          : Promise.resolve([]),
        hasPerm(user, "enrollment_request", "view")
          ? api.get("/admin/enrollment-requests?status=pending").then((r) => r.data?.requests || []).catch(() => [])
          : Promise.resolve([]),
        hasPerm(user, "enrollment_request", "view")
          ? api.get("/admin/payments?status=pending_verification").then((r) => r.data?.payments || []).catch(() => [])
          : Promise.resolve([]),
      ]);
      setRegistrations(Array.isArray(regRes) ? regRes : []);
      setEnrollments(Array.isArray(enrRes) ? enrRes : []);
      setPayments(Array.isArray(payRes) ? payRes : []);
    } catch (e) {
      toast.error("Could not load approvals inbox");
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const setBusy = (id, v) => setBusyIds((prev) => ({ ...prev, [id]: v }));

  const approveRegistration = async (row) => {
    if (!canApprovePending) return;
    setBusy(row.id, true);
    try {
      await api.post(`/users/${row.id}/approve`);
      toast.success(`Approved ${row.full_name || row.email}`);
      setRegistrations((prev) => prev.filter((r) => r.id !== row.id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approve failed");
    } finally {
      setBusy(row.id, false);
    }
  };

  const rejectRegistration = async (row) => {
    if (!canRejectPending) return;
    if (!window.confirm(`Reject ${row.full_name || row.email}?`)) return;
    setBusy(row.id, true);
    try {
      await api.post(`/users/${row.id}/reject`);
      toast.success(`Rejected ${row.full_name || row.email}`);
      setRegistrations((prev) => prev.filter((r) => r.id !== row.id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reject failed");
    } finally {
      setBusy(row.id, false);
    }
  };

  // Unified list, sorted by timestamp descending, tagged by type.
  const unified = useMemo(() => {
    const rows = [
      ...registrations.map((r) => ({
        type: "registration",
        id: r.id,
        primary: r.full_name || r.email || "Student",
        secondary: r.email || "",
        course: r.selected_course_name || null,
        when: r.created_at || r.updated_at,
        raw: r,
      })),
      ...enrollments.map((e) => ({
        type: "enrollment",
        id: e.id,
        primary: e.student_name || e.student_email || "Student",
        secondary: e.student_email || "",
        course: e.course_name || null,
        when: e.requested_at,
        raw: e,
      })),
      ...payments.map((p) => ({
        type: "payment",
        id: p.id,
        primary: p.student_name || p.student_email || "Student",
        secondary: p.student_email || "",
        course: p.course_name || null,
        when: p.submitted_at,
        raw: p,
      })),
    ];
    return rows.sort((a, b) => (b.when || "").localeCompare(a.when || ""));
  }, [registrations, enrollments, payments]);

  const totalCount = unified.length;

  const renderRow = (row) => {
    const meta = TYPE_STYLES[row.type];
    const Icon = meta.icon;
    const busy = !!busyIds[row.id];
    return (
      <li key={`${row.type}-${row.id}`} className="p-4 flex items-start gap-3" data-testid={`approval-row-${row.type}-${row.id}`}>
        <div className={`h-10 w-10 rounded-lg grid place-items-center ${meta.tone} border shrink-0`}>
          <Icon className="w-5 h-5" strokeWidth={1.75} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-0.5">
            <Badge variant="outline" className={`${meta.tone} border font-semibold text-[10px] uppercase tracking-wider`}>
              {meta.label}
            </Badge>
            <span className="text-[11px] text-slate-500">{formatWhen(row.when)}</span>
          </div>
          <div className="font-semibold text-sm text-slate-900 dark:text-slate-100 truncate">{row.primary}</div>
          <div className="text-xs text-slate-500 truncate">{row.secondary}</div>
          {row.course && (
            <div className="text-xs text-slate-700 dark:text-slate-300 mt-1">
              <span className="text-slate-500">Course:</span> <span className="font-medium">{row.course}</span>
            </div>
          )}
          {row.type === "payment" && row.raw?.amount_paid != null && (
            <div className="text-xs text-slate-600 mt-0.5">
              <span className="text-slate-500">Amount:</span> ₱{row.raw.amount_paid}
              {row.raw?.is_renewal && <Badge className="ml-2 bg-violet-100 text-violet-800 border-violet-200 text-[10px] py-0 px-1.5">Renewal</Badge>}
            </div>
          )}
        </div>
        <div className="flex flex-col items-end gap-1.5 shrink-0">
          {row.type === "registration" ? (
            <>
              {canApprovePending && (
                <Button
                  size="sm"
                  className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
                  onClick={() => approveRegistration(row.raw)}
                  disabled={busy}
                  data-testid={`approval-approve-${row.id}`}
                >
                  {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve</>}
                </Button>
              )}
              {canRejectPending && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-8 text-rose-600 hover:text-rose-700 hover:bg-rose-50 text-xs"
                  onClick={() => rejectRegistration(row.raw)}
                  disabled={busy}
                  data-testid={`approval-reject-${row.id}`}
                >
                  <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                </Button>
              )}
            </>
          ) : (
            <Button
              size="sm"
              variant="outline"
              className="h-8 text-xs"
              onClick={() => nav(row.type === "enrollment" ? "/admin/enrollment-requests" : "/admin/payments")}
              data-testid={`approval-review-${row.id}`}
            >
              Review <ArrowRight className="w-3.5 h-3.5 ml-1" />
            </Button>
          )}
        </div>
      </li>
    );
  };

  return (
    <div className="space-y-6" data-testid="approvals-inbox-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-600 flex items-center gap-2">
            <Inbox className="w-3.5 h-3.5" /> Approvals Inbox
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">
            {totalCount} item{totalCount === 1 ? "" : "s"} awaiting review
          </h1>
          <p className="text-slate-600 dark:text-slate-300 mt-1 max-w-2xl">
            Registrations, enrollment requests, and payment verifications — all in one queue.
            Approve registrations in one click, or open the source page for a full review.
          </p>
        </div>
        <Button variant="outline" onClick={loadAll} disabled={loading} data-testid="approvals-refresh-btn">
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          Refresh
        </Button>
      </div>

      <Tabs defaultValue="all" className="w-full" data-testid="approvals-tabs">
        <TabsList className="h-auto p-1 bg-slate-100 dark:bg-slate-800 rounded-xl w-full sm:w-auto grid grid-cols-4 sm:inline-flex sm:gap-1">
          <TabsTrigger value="all" className="h-9 px-3 sm:px-5 text-sm font-semibold" data-testid="approvals-tab-all">
            All
            {totalCount > 0 && <span className="ml-1.5 text-[10px] tabular-nums text-slate-500">{totalCount}</span>}
          </TabsTrigger>
          <TabsTrigger value="registration" className="h-9 px-3 sm:px-5 text-sm font-semibold" data-testid="approvals-tab-registration">
            Registrations
            {registrations.length > 0 && <span className="ml-1.5 text-[10px] tabular-nums text-slate-500">{registrations.length}</span>}
          </TabsTrigger>
          <TabsTrigger value="enrollment" className="h-9 px-3 sm:px-5 text-sm font-semibold" data-testid="approvals-tab-enrollment">
            Enrollments
            {enrollments.length > 0 && <span className="ml-1.5 text-[10px] tabular-nums text-slate-500">{enrollments.length}</span>}
          </TabsTrigger>
          <TabsTrigger value="payment" className="h-9 px-3 sm:px-5 text-sm font-semibold" data-testid="approvals-tab-payment">
            Payments
            {payments.length > 0 && <span className="ml-1.5 text-[10px] tabular-nums text-slate-500">{payments.length}</span>}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="mt-6" data-testid="approvals-tab-content-all">
          <ApprovalsList rows={unified} renderRow={renderRow} loading={loading} />
        </TabsContent>
        <TabsContent value="registration" className="mt-6" data-testid="approvals-tab-content-registration">
          <ApprovalsList rows={unified.filter((r) => r.type === "registration")} renderRow={renderRow} loading={loading} />
        </TabsContent>
        <TabsContent value="enrollment" className="mt-6" data-testid="approvals-tab-content-enrollment">
          <ApprovalsList rows={unified.filter((r) => r.type === "enrollment")} renderRow={renderRow} loading={loading} />
        </TabsContent>
        <TabsContent value="payment" className="mt-6" data-testid="approvals-tab-content-payment">
          <ApprovalsList rows={unified.filter((r) => r.type === "payment")} renderRow={renderRow} loading={loading} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ApprovalsList({ rows, renderRow, loading }) {
  if (loading) {
    return (
      <Card className="p-8 text-center text-slate-400" data-testid="approvals-loading">
        <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
        Loading…
      </Card>
    );
  }
  if (rows.length === 0) {
    return (
      <Card className="p-10 border-dashed border-slate-300 text-center" data-testid="approvals-empty">
        <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto mb-2" />
        <div className="font-display font-bold text-lg">All clear!</div>
        <div className="text-sm text-slate-500 mt-1">Nothing awaits your review right now.</div>
      </Card>
    );
  }
  return (
    <Card className="p-0 border-slate-200 dark:border-slate-800 overflow-hidden">
      <ul className="divide-y divide-slate-100 dark:divide-slate-800">
        {rows.map(renderRow)}
      </ul>
    </Card>
  );
}
